import { ArrowRight, Check } from 'lucide-react';
import { useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { Field } from '@/components/field';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { api, errorMessage } from '@/lib/api';
import { browserCountry, countries } from '@/lib/countries';
import { t } from '@/lib/i18n';

const empty = () => ({ name: '', email: '', company: '', country: browserCountry() ?? '', phone: '', message: '', website: '' });

/** "Request access": a short form whose answer becomes a lead in Leetz (control plane `POST /api/access-requests`). */
export function RequestAccess({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const countryList = useMemo(() => countries(), []);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = (field: keyof typeof form) => (value: string) => setForm((f) => ({ ...f, [field]: value }));

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!form.country) return setError(t('Choose your country.'));
    setBusy(true);
    try {
      await api.auth.requestAccess({
        name: form.name,
        email: form.email.trim(),
        company: form.company,
        country: form.country,
        phone: form.phone,
        message: form.message.trim() || undefined,
        pageUrl: window.location.href,
        website: form.website || undefined,
      });
      setSent(true);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  function onOpenChange(next: boolean) {
    setOpen(next);
    if (!next && sent) {
      setSent(false);
      setForm(empty());
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent>
        {sent ? (
          <div className="flex flex-col items-center gap-3 py-4 text-center">
            <span className="flex size-11 items-center justify-center rounded-full bg-primary/10 text-primary">
              <Check className="size-5" />
            </span>
            <DialogTitle>{t('Thanks, {name}!', { name: form.name.split(' ')[0] ?? '' })}</DialogTitle>
            <DialogDescription>{t('We’ll get back to you at {email} to set up your first desk.', { email: form.email.trim() })}</DialogDescription>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>{t('Request access')}</DialogTitle>
              <DialogDescription>{t('Tell us who you are. We’ll reach out to set up your first desk with you.')}</DialogDescription>
            </DialogHeader>
            <form onSubmit={onSubmit} className="grid gap-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label={t('Your name')}>
                  <Input autoComplete="name" required maxLength={120} value={form.name} onChange={(e) => set('name')(e.target.value)} />
                </Field>
                <Field label={t('Company')}>
                  <Input autoComplete="organization" required maxLength={120} value={form.company} onChange={(e) => set('company')(e.target.value)} />
                </Field>
              </div>
              <Field label={t('Work email')}>
                <Input type="email" autoComplete="email" required maxLength={200} value={form.email} onChange={(e) => set('email')(e.target.value)} placeholder={t('you@company.com')} />
              </Field>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label={t('Country')}>
                  <Select value={form.country} onValueChange={set('country')}>
                    <SelectTrigger>
                      <SelectValue placeholder={t('Choose…')} />
                    </SelectTrigger>
                    <SelectContent>
                      {countryList.map((c) => (
                        <SelectItem key={c.code} value={c.code}>
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label={t('Phone')}>
                  <Input type="tel" autoComplete="tel" required value={form.phone} onChange={(e) => set('phone')(e.target.value)} placeholder="+49 30 1234567" />
                </Field>
              </div>
              <Field label={t('What would you hand off first?')} optional>
                <Textarea
                  rows={3}
                  maxLength={2000}
                  value={form.message}
                  onChange={(e) => set('message')(e.target.value)}
                  placeholder={t('e.g. first-level support, SEO, lead follow-up')}
                />
              </Field>
              {/* Honeypot: off screen and out of the tab order, so only bots fill it in. */}
              <input
                type="text"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden
                className="absolute -left-[9999px] h-0 w-0 opacity-0"
                value={form.website}
                onChange={(e) => set('website')(e.target.value)}
              />
              {error && (
                <p role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                  {error}
                </p>
              )}
              <Button type="submit" loading={busy} className="w-full">
                {t('Request access')} <ArrowRight />
              </Button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
